import {
  AccessTime,
  Analytics,
  Dashboard,
  EventNote,
  Settings,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { useScreenSize } from "../../hooks/useScreenSize";
import { CustomerIcon } from "../../icons/CustomerIcon/CustomerIcon";
import { DefectIcon } from "../../icons/DefectIcon/DefectIcon";
import { DocumenteIcon } from "../../icons/DocumenteIcon/DocumenteIcon";
import { MonitorsIcon } from "../../icons/MonitorsIcon/MonitorIcon";
import { TourIcon } from "../../icons/TourIcon/TourIcon";
import { RootState } from "../../store";
import { appointmentPropertyAreasActions } from "../../store/appointment-property-areas-slice";
import { resetState } from "../../store/appointment-slice";
import { checkIsRunning } from "../../store/clock-slice";
import { creatingDeficiencyActions } from "../../store/deficiencies-slice";
import { creatingDocumentationActions } from "../../store/documentation-slice";
import { overviewAppointmentSliceActions } from "../../store/overview-appointment-slice";
import { productSliceActions } from "../../store/products-slice";
import { propertyActions } from "../../store/property-slice";
import { servicedMonitorsActions } from "../../store/serviced-monitors";
import { activeTabsActions } from "../../store/tabs-slice";
import { creatingTaskActions } from "../../store/task-slice";

import Logo from "./desktop-logo.png";
import styles from "./navigation.module.css";

type NavigationLink = {
  to: string;
  component: JSX.Element;
  label: string;
};

const NAV_LINKS: NavigationLink[] = [
  { to: "/", component: <TourIcon />, label: "Assignments" },
  { to: "/time", component: <AccessTime />, label: "Time" },
  {
    to: "/customerinsight",
    component: <CustomerIcon />,
    label: "Customers",
  },
  { to: "/calendar", component: <EventNote />, label: "Absences" },
  { to: "/settings", component: <Settings />, label: "Settings" },
];

const CUSTOMER_NAV_LINKS: NavigationLink[] = [
  { to: "/", component: <Dashboard />, label: "LastServices" },
  {
    to: "/deficiencies",
    component: <DefectIcon />,
    label: "Defects",
  },
  { to: "/monitors", component: <MonitorsIcon />, label: "Monitore" },
  { to: "/analysis", component: <Analytics />, label: "Analysis" },
  { to: "/documente", component: <DocumenteIcon />, label: "Dokumente" },
  { to: "/settings", component: <Settings />, label: "Settings" },
];

export const Navigation = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const { isDesktop } = useScreenSize();
  const isCustomerAdmin = useSelector(
    (state: RootState) => state.user.isCustomerAdmin
  );

  const currentPage = useSelector(
    (state: RootState) => state.tabs.dashboard.url
  );

  const appointment = useSelector((state: RootState) => state.appointment);
  const user = useSelector((state: RootState) => state.user.currentUser.user);
  const isRunning = useSelector((state: RootState) =>
    checkIsRunning(state, appointment.id, user!.id)
  );

  const handleNavClick = (navigateTo?: string, extraCallback?: () => void) => {
    dispatch(creatingDocumentationActions.resetState());

    if (extraCallback) extraCallback();

    if (navigateTo) navigate(navigateTo);
  };

  const handleDashboardNavigation = () => {
    dispatch(activeTabsActions.setMonitorsStep(1));
    dispatch(activeTabsActions.setDocumentsStep(1));
    dispatch(activeTabsActions.setProperyInsightTab("Monitors"));
    dispatch(activeTabsActions.setDeficienciesStep(1));
    dispatch(creatingTaskActions.resetAppointment());
    dispatch(servicedMonitorsActions.resetState());
    dispatch(propertyActions.setViewOnly(false));

    if (location.pathname === "/") {
      dispatch(propertyActions.setViewOnly(false));
      dispatch(overviewAppointmentSliceActions.resetState());
      dispatch(appointmentPropertyAreasActions.resetState());
      dispatch(creatingDocumentationActions.resetState());
      dispatch(creatingDeficiencyActions.resetState());
      dispatch(productSliceActions.resetState());

      if (!isRunning) {
        dispatch(resetState());
      }
    } else if (currentPage !== "/") {
      navigate(currentPage);
      dispatch(activeTabsActions.setDashbaordUrl("/"));
    } else {
      navigate("/");
    }
  };
  const getNavLinkClassName = ({ isActive, isPending }: any) =>
    isPending ? styles.pending : isActive ? styles.active : "";

  const LINKS = isCustomerAdmin ? CUSTOMER_NAV_LINKS : NAV_LINKS;
  const mainLinks = isDesktop
    ? LINKS.filter((link) => link.label !== "Settings")
    : LINKS;

  const settingsLink = LINKS.find((link) => link.label === "Settings");

  return (
    <div className={styles["dashboard-navigation"]}>
      {isDesktop && (
        <img src={Logo} className={styles["dashboard-navigation__logo"]} />
      )}
      <div className={styles["dashboard-navigation-links"]}>
        {mainLinks.map(({ to, component, label }, i) => {
          if (to === "/") {
            return (
              <button
                key={i}
                className={`${styles.navLinkButton} ${getNavLinkClassName}`}
                onClick={handleDashboardNavigation}
              >
                {component}
                {isDesktop && (
                  <label
                    className={styles["dashboard-navigation-links__label"]}
                  >
                    {t(label)}
                  </label>
                )}
              </button>
            );
          }

          return (
            <NavLink
              key={i}
              to={to}
              className={getNavLinkClassName}
              onClick={() => handleNavClick(to)}
            >
              {component}
              {isDesktop && (
                <label className={styles["dashboard-navigation-links__label"]}>
                  {t(label)}
                </label>
              )}
            </NavLink>
          );
        })}
      </div>
      {isDesktop && settingsLink && (
        <div
          className={`${styles["dashboard-navigation-links"]} ${styles["dashboard-navigation-settings"]}`}
        >
          <NavLink to={settingsLink.to} className={getNavLinkClassName}>
            {settingsLink.component}
            <label className={styles["dashboard-navigation-links__label"]}>
              {t(settingsLink.label)}
            </label>
          </NavLink>
        </div>
      )}
    </div>
  );
};
